import React, { useState, useEffect, useRef } from "react";
import Skeleton from "react-loading-skeleton";
import { GetGroupUserInfo } from "../../../../utils/GroupNotificationUtils";
import { User } from "../../../../features/user/userSlice";
import useGetLanguage from "../../../../utils/useGetLanguage";
import { LanguageText } from "../../../../dictionary/GroupsText";

interface UserFilterProps {
  value: string;
  onChange: (value: string) => void;
  groupId: string;
  uniqueUserIds: string[];
  onUserSelect?: (userId: string, userEmail: string) => void; // Add callback for user selection
}

const UserFilter: React.FC<UserFilterProps> = ({
  value,
  onChange,
  groupId,
  uniqueUserIds,
  onUserSelect,
}) => {
  const Language: LanguageText = useGetLanguage({
    fileName: "GroupsText",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleUserSelect = (user: User) => {
    if (!user || !user.id) {
      console.error("Invalid user data:", user);
      return;
    }
    onChange(user.email || "");

    if (onUserSelect) {
      onUserSelect(user.id, user.email || "");
    }

    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClear = () => {
    onChange("");
    setSearchTerm("");
    setIsOpen(false);
  };

  const allUsers = uniqueUserIds
    .map((userId) => {
      const { data: userInfo, isLoading } = GetGroupUserInfo({
        groupid: groupId,
        userid: userId,
      });
      return { userInfo, isLoading, userId };
    })
    .filter((item) => item.userInfo || item.isLoading); // Include both loaded and loading users

  // Filter users based on search term
  const filteredUsers = searchTerm.trim()
    ? allUsers.filter((item) => {
        if (!item.userInfo) return false; // Skip loading users when filtering
        return (
          item.userInfo.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.userInfo.email?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      })
    : allUsers;

  const selectedUser = allUsers.find((item) => item.userInfo?.email === value);

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {Language?.groupAuditLogs?.user}
      </label>
      <div className="relative">
        <input
          type="text"
          placeholder={Language?.groupAuditLogs?.filterByUser}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setIsOpen(true)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <div
          className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer"
          onClick={() => setIsOpen(!isOpen)}
        >
          <svg
            className="h-5 w-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {filteredUsers.length > 0 ? (
            <div>
              {filteredUsers.map((item) => {
                if (item.isLoading) {
                  return (
                    <div key={item.userId} className="px-3 py-2">
                      <Skeleton height={16} width="100%" />
                    </div>
                  );
                }

                if (!item.userInfo) return null;

                return (
                  <div
                    key={item.userInfo.id || item.userId}
                    onClick={() => handleUserSelect(item.userInfo)}
                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                  >
                    <div className="font-medium">
                      {item.userInfo.username || "Unknown User"}
                    </div>
                    <div className="text-gray-500 text-xs">{item.userInfo.email}</div>
                  </div>
                );
              })}
            </div>
          ) : searchTerm.trim() ? (
            <div className="px-3 py-2 text-sm text-gray-500">No users found</div>
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500">Type to search users</div>
          )}
        </div>
      )}

      {value && selectedUser?.userInfo && (
        <div className="mt-2 flex items-center">
          <span className="text-sm text-gray-600">
            {Language?.groupAuditLogs?.selected}: {selectedUser.userInfo.email}
          </span>
          <button
            onClick={handleClear}
            className="ml-2 text-sm text-red-600 hover:text-red-800"
          >
            {Language?.groupAuditLogs?.clear}
          </button>
        </div>
      )}
    </div>
  );
};

export default UserFilter;
